import { Cable, Check, ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '~/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { ConnectorIcon } from '~/features/integrations/components/connector-icon';
import type {
  CatalogConnection,
  CatalogSource,
} from '~/features/product-catalog/hooks/use-catalog-connections';
import { cn } from '~/utils/cn';

interface CatalogPickerProps {
  catalogs: CatalogConnection[];
  /** Selected source token (`undefined` while catalogs are still resolving). */
  value: CatalogSource | undefined;
  onChange: (source: CatalogSource) => void;
  isLoading?: boolean;
  /** Extra classes for the trigger button (e.g. a compact width at a breakpoint). */
  triggerClassName?: string;
  /** Extra classes for the trigger's text label — lets a host collapse it to an
   *  icon-only trigger at a breakpoint (e.g. `max-[1199px]:hidden`). */
  labelClassName?: string;
}

/**
 * Searchable picker for the imported catalogs (Google / Meta). Selecting a
 * catalog re-scopes the products table by connection id; the parent owns the
 * selected `source` (mirrored to the URL).
 */
export function CatalogPicker({
  catalogs,
  value,
  onChange,
  isLoading,
  triggerClassName,
  labelClassName,
}: CatalogPickerProps) {
  const { t } = useTranslation('product-catalog');
  const [open, setOpen] = useState(false);
  const selected = catalogs.find((c) => c.source === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={isLoading}
          className={cn(
            'w-72 cursor-pointer justify-between font-normal disabled:cursor-not-allowed',
            triggerClassName,
          )}
        >
          {selected ? (
            <span className="flex min-w-0 items-center gap-2">
              <ConnectorIcon
                type={selected.connectorCode}
                iconUrl={selected.iconUrl}
                className="h-4 w-4"
              />
              <span className={cn('truncate', labelClassName)}>{selected.name}</span>
            </span>
          ) : (
            <span className="text-muted-foreground flex min-w-0 items-center gap-2">
              <Cable aria-hidden="true" className="h-4 w-4 shrink-0" />
              <span className={cn('truncate', labelClassName)}>
                {isLoading ? t('picker.loading') : t('picker.placeholder')}
              </span>
            </span>
          )}
          <ChevronsUpDown className="text-muted-foreground h-4 w-4 shrink-0 opacity-70" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command>
          <CommandInput placeholder={t('picker.search')} />
          <CommandList>
            <CommandEmpty>{t('picker.empty')}</CommandEmpty>
            <CommandGroup>
              {catalogs.map((catalog) => (
                <CommandItem
                  key={catalog.connectionId}
                  value={`${catalog.name} ${catalog.connectorCode} ${catalog.source}`}
                  onSelect={() => {
                    onChange(catalog.source);
                    setOpen(false);
                  }}
                  className="cursor-pointer gap-2"
                >
                  <ConnectorIcon
                    type={catalog.connectorCode}
                    iconUrl={catalog.iconUrl}
                    className="h-4 w-4"
                  />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-sm font-medium">{catalog.name}</span>
                    <span className="text-muted-foreground truncate text-xs">
                      {catalog.connectorCode.replace(/_/g, ' ')}
                    </span>
                  </span>
                  <Check
                    className={cn(
                      'ml-auto h-4 w-4 shrink-0',
                      catalog.source === value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
